







































import { computed, defineComponent, ref, watch } from "@vue/composition-api";
import { InputVaultFileSearch, VaultCategory, VaultFile } from "@scrile/api-provider/dist/api/VaultsProvider";
import useAuthUser from "@/hooks/useAuthUser";
import usePager from "@/hooks/usePager";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t, $tc } from "@/lib/i18nHelpers";
import { UploadVaultFile } from "@/types";
import { EVENTS } from "@/const";
import VaultCategories from "./components/VaultCategories";
import VaultManage from "./components/VaultManage";
import { ANY_POST_SUBJECT, defaultSortType } from "./const";
import { useVaultCategory } from "./hooks/useVaultCategory";
import { useVaultFiles, VaultUploadHandlers } from "./hooks/useVaultFiles";
import { FilterName, SortType } from "./types";

export default defineComponent({
  name: "VaultController",
  components: {
    VaultManage,
    VaultCategories,
  },
  setup(props, { emit }) {
    const { authUserStrict } = useAuthUser();

    const selectedCategoryId = ref("");
    const filesLoading = ref(false);
    const sortType = ref(SortType.LAST_USE_TIME_DESC);
    // "Unused" in the Vault itself means the same as when files are picked for a post: in none of the posts
    const filterName = ref(FilterName.ALL);
    const vaultFilesCount = ref(0);

    const {
      vaultCategories,
      categoryScroll,
      vaultCategoriesCount,
      getFirstCategoryPage,
      getNextCategoryPage,
      updateCategory,
      removeCategory,
      updateCategoryPreviewFiles,
      iterativelyFindCategory,
      getCategoryPreview,
    } = useVaultCategory();

    const {
      enableScroll: fileScroll,
      items: vaultFiles,
      data: filesData,
      getNextPage: getNextFilePage,
      getFirstPage: getFirstFilePage,
      removeItem,
      updateItem,
      addItems,
    } = usePager<VaultFile, InputVaultFileSearch>((page, data) => {
      return providers.VaultsProvider.search({
        data: {
          userId: authUserStrict.value.id,
          categoryIds: [selectedCategoryId.value],
          page,
          sort: sortType.value,
          unusedIn: filterName.value === FilterName.UNUSED ? ANY_POST_SUBJECT : undefined,
          ...data,
        },
      });
    });

    const {
      deleteLoading,
      moveLoading,
      uploadingFiles,
      uploadStatus,
      moveFiles,
      deleteFiles,
      addFiles,
      retryFile,
      retryFailed,
      forgetFailed,
    } = useVaultFiles(selectedCategoryId);

    const selectedCategory = computed(() => vaultCategories.value.find(i => i.id === selectedCategoryId.value) ?? null);

    const handleSort = (newSortType: SortType) => {
      sortType.value = newSortType;
    };

    // the folder watcher loads its files with the new sort, the sort watcher must not load them a second time
    let sortChangedWithFolder = false;
    const selectCategory = (categoryId: string, category?: VaultCategory) => {
      if (categoryId) {
        const newSortType = defaultSortType(category ?? vaultCategories.value.find(i => i.id === categoryId));
        sortChangedWithFolder = newSortType !== sortType.value;
        sortType.value = newSortType;
      }
      selectedCategoryId.value = categoryId;
    };

    const uploadHandlers: VaultUploadHandlers = {
      onSuccess: (uploadUuid, file) => {
        updateCategoryPreviewFiles(selectedCategoryId.value, file);
        updateItem(file, f => f.uuid === uploadUuid);
      },
      onDuplicate: (uploadUuid, existingFile) => {
        removeItem(f => f.uuid === uploadUuid);
        updateItem(existingFile, f => f.id === existingFile.id);
      },
      onProgress: (file, uploadProgress) => {
        updateItem({ ...file, uploadProgress } as VaultFile, f => f.uuid === file.uuid);
      },
      onError: file => {
        updateItem(file as VaultFile, f => f.uuid === file.uuid);
      },
      onRetry: (oldUuid, file) => {
        updateItem(file as VaultFile, f => f.uuid === oldUuid);
      },
    };

    const onAddFiles = async (files: FileList | File[]) => {
      // the tiles come in name order (see addFiles); on top of the folder in the order of its current sort,
      // the folder is reloaded in that sort when the upload is over
      const uploadVaultFiles = await addFiles(files, uploadHandlers);
      const descending = [SortType.LAST_USE_TIME_DESC, SortType.FILE_NAME_DESC].includes(sortType.value);
      addItems(descending ? [...uploadVaultFiles].reverse() : uploadVaultFiles);
      const skipped = files.length - uploadVaultFiles.length;
      if (skipped > 0) {
        emitter.emit(EVENTS.NOTIFICATION_SHOW, {
          text: $t("{filesCount} already in this folder, skipped", { filesCount: $tc($t("file"), skipped) }),
        });
      }
    };

    const onRetryFile = (file: UploadVaultFile) => retryFile(file.uuid, uploadHandlers);

    const onRetryFailed = () => retryFailed(uploadHandlers);

    const onFilesMove = async (
      { files, categoryId }: { files: UploadVaultFile[]; categoryId: string },
      cleanCheckedFiles?: () => void
    ) => {
      const movedFileIds = files.map(f => f.id);
      const response = await moveFiles(movedFileIds, categoryId);
      if (response) {
        emitter.emit(EVENTS.NOTIFICATION_SHOW, {
          text: $t(`{filesCount} ${movedFileIds.length === 1 ? "has" : "have"} been moved`, {
            filesCount: $tc($t("file"), movedFileIds.length),
          }),
        });
        await Promise.all([
          removeItem(i => movedFileIds.includes(i.id)),
          getCategoryPreview([selectedCategoryId.value, categoryId]),
        ]);
        cleanCheckedFiles && cleanCheckedFiles();
      }
    };

    const onFilesDelete = async (files: UploadVaultFile[], cleanCheckedFiles?: () => void) => {
      const deletedFileIds = files.map(f => f.id);
      const response = await deleteFiles(deletedFileIds);
      if (response) {
        emitter.emit(EVENTS.NOTIFICATION_SHOW, {
          text: $t(`{filesCount} ${deletedFileIds.length === 1 ? "has" : "have"} been deleted`, {
            filesCount: $tc($t("file"), deletedFileIds.length),
          }),
        });
        await Promise.all([
          removeItem(i => deletedFileIds.includes(i.id)),
          getCategoryPreview([selectedCategoryId.value]),
        ]);
        cleanCheckedFiles && cleanCheckedFiles();
      }
    };

    const onCategoryCreate = async (category: VaultCategory) => {
      selectCategory(category.id, category);
      await iterativelyFindCategory(category.id);
    };

    const onCategoryUpdate = (category: VaultCategory) => {
      selectedCategoryId.value = category.id;
      updateCategory(category);
    };

    const onCategoryDelete = async () => {
      await removeCategory(selectedCategoryId.value);
      handleSort(SortType.LAST_USE_TIME_DESC);
      await getFirstCategoryPage();
      selectedCategoryId.value = "";
    };

    const getFiles = async () => {
      try {
        filesLoading.value = true;
        await getFirstFilePage();
      } finally {
        filesLoading.value = false;
      }
    };

    const getTotalFilesCount = async () => {
      vaultFilesCount.value = (
        await providers.VaultsProvider.search({ data: { userId: authUserStrict.value.id, size: 1 } }, ["id"])
      ).totalRecords;
    };

    watch(
      selectedCategoryId,
      async currentValue => {
        forgetFailed();
        try {
          emit("loading", true);
          const requests = [];
          if (currentValue) {
            requests.push(getFiles());
          } else {
            requests.push(getTotalFilesCount());
            !vaultCategories.value.length && requests.push(getFirstCategoryPage());
          }
          await Promise.all(requests);
        } finally {
          emit("loading", false);
        }
      },
      { immediate: true }
    );

    // the tiles of a finished upload stand on top of the folder: reload it in its sort. Failed tiles are kept for
    // "Retry", so the folder is not reloaded while there are any
    watch(uploadingFiles, (uploading, wasUploading) => {
      if (uploading || !wasUploading || !selectedCategoryId.value || uploadStatus.value.failed > 0) return;
      getFirstFilePage();
    });

    watch([sortType, filterName], () => {
      if (sortChangedWithFolder) {
        sortChangedWithFolder = false;
        return;
      }
      if (!selectedCategoryId.value) return;
      getFiles();
    });

    // "Select all" in a folder (CO-15602): every page of the folder, then the caller selects them
    const loadAllFiles = async (onLoaded: (files: VaultFile[]) => void) => {
      let idleTries = 0;
      while (filesData.value?.hasNextPage && idleTries < 50) {
        const loaded = vaultFiles.value.length;
        await getNextFilePage();
        if (vaultFiles.value.length > loaded) {
          idleTries = 0;
        } else {
          // a page is being loaded by the infinite scroll, wait for it
          idleTries++;
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      onLoaded(vaultFiles.value);
    };

    return {
      moveLoading,
      deleteLoading,
      filesLoading,
      fileScroll,
      categoryScroll,
      selectedCategory,
      selectedCategoryId,
      uploadingFiles,
      uploadStatus,
      vaultCategories,
      vaultCategoriesCount,
      vaultFiles,
      vaultFilesCount,
      sortType,
      filterName,
      getNextCategoryPage,
      getNextFilePage,
      loadAllFiles,
      onCategoryCreate,
      onCategoryUpdate,
      onCategoryDelete,
      onFilesDelete,
      onAddFiles,
      onRetryFile,
      onRetryFailed,
      onFilesMove,
      handleSort,
      selectCategory,
    };
  },
});
